import React from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import MarketingLayout from "@/components/MarketingLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function Community() {
  const { user } = useAuth();
  const Shell = user ? DashboardLayout : MarketingLayout;
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api.get("/community/projects").then(r => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <Shell>
      <div className="max-w-[1200px] mx-auto px-8 py-16">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Community</div>
        <h1 className="font-editorial text-5xl leading-tight mb-8">Published stories</h1>
        {loading ? <div className="text-[#A1A1AA]">Loading…</div> :
          projects.length === 0 ? (
            <div className="border border-[#1E1E1E] bg-[#0F0F10] p-12 text-center">
              <div className="font-editorial text-2xl">Nothing published yet.</div>
              <p className="text-sm text-[#A1A1AA] mt-2">Be the first to publish. Open any of your projects and press &ldquo;Publish to community&rdquo;.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((p) => (
                <Link key={p.id} to={`/story/${p.slug || p.id}`}
                  className="group block border border-[#1E1E1E] bg-[#141414] p-6 hover:border-[#D4AF37] transition-colors"
                  data-testid={`community-project-${p.id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#71717A]">{p.genre} · {p.format}</div>
                  <div className="font-editorial text-2xl mt-2 group-hover:text-[#D4AF37] transition-colors">{p.title}</div>
                  <div className="text-sm text-[#A1A1AA] mt-3 line-clamp-3 leading-relaxed">{p.logline}</div>
                </Link>
              ))}
            </div>
          )
        }
      </div>
    </Shell>
  );
}

export function Settings() {
  const { user } = useAuth();
  return (
    <DashboardLayout>
      <div className="max-w-2xl px-8 py-10">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Settings</div>
        <h1 className="font-editorial text-4xl mb-8">Account</h1>
        <div className="border border-[#1E1E1E] bg-[#141414] p-6 space-y-4">
          <Row label="Name" value={user?.name} />
          <Row label="Email" value={user?.email} />
          <Row label="Role" value={user?.role} />
          <Row label="Plan" value={user?.plan} />
          <Row label="Credits" value={user?.credits} />
        </div>
      </div>
    </DashboardLayout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-[#1E1E1E] pb-3 last:border-0">
      <div className="text-xs uppercase tracking-widest text-[#71717A]">{label}</div>
      <div className="text-sm text-[#FFFFF0]">{value}</div>
    </div>
  );
}

export function Docs() {
  return (
    <MarketingLayout>
      <div className="max-w-3xl mx-auto px-8 py-20">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Docs</div>
        <h1 className="font-editorial text-5xl mb-10">Getting started</h1>
        <div className="prose prose-invert space-y-8 text-[#E4E4E7]">
          <Section title="1. Create your first project">
            Open your Projects page and click <span className="text-[#D4AF37]">New Project</span>. Give it a title, a logline, a genre and a format. StoryFrame will scaffold the screenplay and workspace around it.
          </Section>
          <Section title="2. Write the screenplay">
            The editor uses structured nodes (Scene, Action, Character, Parenthetical, Dialogue, Transition). Press <kbd>Enter</kbd> to add a new line — StoryFrame picks the next likely type. Press <kbd>Tab</kbd> to cycle through types.
          </Section>
          <Section title="3. Let the AI Writer help">
            Select any text and click the sparkle. Choose an action — <em>continue</em>, <em>rewrite</em>, <em>tighten</em>, <em>increase tension</em>. AI proposes; you accept or reject.
          </Section>
          <Section title="4. Diagnose with Script Doctor">
            Open <span className="text-[#D4AF37]">AI Studio</span> and run the Script Doctor. Get scores, issues and recommendations tied to real scenes.
          </Section>
          <Section title="5. Generate storyboards">
            Open a scene in Storyboard Studio. Run the AI Director to propose 4-8 shots. Generate a frame per shot with Gemini Nano Banana. Everything is stored in your Asset Vault.
          </Section>
          <Section title="6. Track production readiness">
            The Production page shows one number — how close your project is to being shootable — backed by every entity you own upstream.
          </Section>
        </div>
      </div>
    </MarketingLayout>
  );
}

export function GenericPage({ title, subtitle }) {
  return (
    <MarketingLayout>
      <div className="max-w-3xl mx-auto px-8 py-24">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">{subtitle || "StoryFrame AI"}</div>
        <h1 className="font-editorial text-5xl mb-8">{title}</h1>
        <p className="text-[#A1A1AA] leading-relaxed">This section is coming soon. In the meantime, explore the workspace or read the getting-started guide.</p>
        <div className="mt-8 flex gap-3">
          <Link to="/signup" className="bg-[#D4AF37] text-[#0A0A0A] px-5 py-2.5 text-sm font-medium">Start creating</Link>
          <Link to="/docs" className="border border-[#27272A] px-5 py-2.5 text-sm">Read the docs</Link>
        </div>
      </div>
    </MarketingLayout>
  );
}

function Section({ title, children }) {
  return (
    <section className="border-l-2 border-[#D4AF37] pl-6">
      <h2 className="font-editorial text-2xl mb-3">{title}</h2>
      <div className="text-sm leading-relaxed text-[#A1A1AA]">{children}</div>
    </section>
  );
}
